import { UniversalNodeRegistry } from '@/features/backtest/schemas/universal-node-schema'
import { materializeNode } from '@/features/backtest/graph-engine/materialize'
import { freshId, isProxyEdge, readParams, type LooseEdge, type LooseNode } from './graph-lowering'

export const SESSION_METRIC_TYPE = 'sessionMetric'

type PriceField = 'open' | 'high' | 'low' | 'close' | 'volume'
type InputEntry = Record<string, unknown> & { name: string }

interface LevelWindow {
  mode: 'first' | 'last' | 'max' | 'min'
  startOffset: number
  startTime: string
  endOffset: number
  endTime: string
  field: PriceField
}

/**
 * The reference levels a metric reads, defined as the Form defines them
 * (form-builder/field-catalog.ts REFERENCE_WINDOWS, which a test keeps these
 * equal to; the catalog is not imported, it reaches chart code, and nothing
 * here is read at load, which tests that stub the presets rely on). A Time Slice
 * is a running value, empty until its window opens: the day's open, high and
 * low so far, never the day's final ones.
 */
export const SESSION_METRIC_WINDOWS: Record<
  'day-open' | 'high-of-day' | 'low-of-day' | 'previous-close',
  LevelWindow
> = {
  'day-open': {
    mode: 'first',
    startOffset: 0,
    startTime: '09:30',
    endOffset: 0,
    endTime: '16:00',
    field: 'open',
  },
  'high-of-day': {
    mode: 'max',
    startOffset: 0,
    startTime: '09:30',
    endOffset: 0,
    endTime: '16:00',
    field: 'high',
  },
  'low-of-day': {
    mode: 'min',
    startOffset: 0,
    startTime: '09:30',
    endOffset: 0,
    endTime: '16:00',
    field: 'low',
  },
  // The previous-close Time Slice preset (time-slice-presets.ts).
  'previous-close': {
    mode: 'last',
    startOffset: 1,
    startTime: '09:30',
    endOffset: 1,
    endTime: '16:00',
    field: 'close',
  },
}

/**
 * The Session Metric, expanded for the engine (Chris, 2026-10-07: "build the
 * compound nodes ourself so that we can then consider adding to the form").
 * The engine has no Session Metric; each one becomes nodes it has, built from
 * the same pieces the Form's operands use, and the last of them keeps the
 * metric's id, so wires out of it and run errors stay on the node the user
 * placed:
 *
 *   volumeToday          Rolling Sum of volume over the session (read from `sum`)
 *   changeFromOpen       Percent Change from the day's open to the close
 *   changeFromPrevClose  Percent Change from the previous close to the close
 *   gap                  Percent Change from the previous close to the day's open
 *   distanceFromVwap     Percent Change from the VWAP to the close
 *   positionInDayRange   Position in Range (Custom): the close between the low
 *                        and high of day, 0 to 100 (NaN while they are equal)
 *
 * Every price comes from the metric's own Price Data wire, field picked per
 * input. Generated ids are `<metricId>__open`, `__prev`, `__high`, `__low`,
 * `__vwap` (numbered when taken); `owners` maps them to their metric. A metric
 * with no wire is left as is: readiness refuses the run first.
 *
 * Pure; returns the input arrays when the graph has no Session Metric.
 */
export function lowerSessionMetrics<N extends LooseNode, E extends LooseEdge>(
  nodes: N[],
  edges: E[],
): { nodes: N[]; edges: E[]; owners: Map<string, string> } {
  const owners = new Map<string, string>()
  const metrics = nodes.filter((n) => n.type === SESSION_METRIC_TYPE)
  if (metrics.length === 0) return { nodes, edges, owners }

  const taken = new Set(nodes.map((n) => n.id))
  const replaced = new Map<string, N>()
  /** The handle each lowered metric's value leaves from, where it is not `value`. */
  const outputOf = new Map<string, string>()
  const added: N[] = []
  const addedEdges: E[] = []

  for (const metric of metrics) {
    const wire = edges.find(
      (e) => e.target === metric.id && e.targetHandle === 'input_1' && !isProxyEdge(e),
    )
    if (!wire) continue
    const at = metric.position ?? { x: 0, y: 0 }

    const make = (id: string, type: string, params: Record<string, unknown>, dy: number) => {
      const schema = UniversalNodeRegistry.getSchema(type)
      // Without it the metric would quietly vanish from the run. Fail loudly instead.
      if (!schema) throw new Error(`Session Metric: the "${type}" node schema is not registered`)
      const { node } = materializeNode(
        { id, type, params: params as Record<string, string | number | boolean> },
        { schemaById: new Map([[id, schema]]), position: { x: at.x - 260, y: at.y + dy } },
      )
      const inputs = [...((node.data as { inputs?: InputEntry[] }).inputs ?? [])]
      return {
        id,
        inputs,
        node: {
          ...node,
          ...(metric.parentId ? { parentId: metric.parentId } : {}),
          data: { ...node.data, inputs },
        } as unknown as N,
      }
    }
    type Made = ReturnType<typeof make>

    // One wire into a generated node, with the input entry serialization reads.
    const connect = (
      target: Made,
      handle: string,
      source: string,
      sourceHandle: string | null | undefined,
      field?: PriceField,
    ) => {
      addedEdges.push({
        id: `e-${source}-${sourceHandle ?? ''}-${target.id}-${handle}`,
        source,
        sourceHandle,
        target: target.id,
        targetHandle: handle,
      } as unknown as E)
      const next: InputEntry = {
        inputType: 'node',
        value: null,
        ...(field ? { field } : {}),
        name: handle,
        source,
      }
      const index = target.inputs.findIndex((i) => i.name === handle)
      if (index >= 0) target.inputs[index] = next
      else target.inputs.push(next)
    }
    const price = (target: Made, handle: string, field?: PriceField) =>
      connect(target, handle, wire.source, wire.sourceHandle, field)

    // A reference level, as a Time Slice over the metric's own price feed.
    const level = (key: keyof typeof SESSION_METRIC_WINDOWS, suffix: string, dy: number) => {
      const { field, ...window } = SESSION_METRIC_WINDOWS[key]
      const made = make(freshId(`${metric.id}__${suffix}`, taken), 'timeSlice', window, dy)
      price(made, 'input_1', field)
      owners.set(made.id, metric.id)
      added.push(made.node)
      return made.id
    }
    // A percent change from `from` to `to` ((B - A) / A x 100: A old, B new).
    const percentChange = () => make(metric.id, 'percentageChange', { inputBType: 'node' }, 0)

    let final: Made
    switch (String(readParams(metric).metric ?? 'changeFromOpen')) {
      case 'volumeToday':
        final = make(metric.id, 'rollingSum', { reference: 'session', period: 20 }, 0)
        price(final, 'input_1', 'volume')
        outputOf.set(metric.id, 'sum')
        break
      case 'changeFromOpen': {
        const open = level('day-open', 'open', -120)
        final = percentChange()
        connect(final, 'input_1', open, 'value')
        price(final, 'input_2', 'close')
        break
      }
      case 'changeFromPrevClose': {
        const prev = level('previous-close', 'prev', -120)
        final = percentChange()
        connect(final, 'input_1', prev, 'value')
        price(final, 'input_2', 'close')
        break
      }
      case 'gap': {
        const prev = level('previous-close', 'prev', -120)
        const open = level('day-open', 'open', 120)
        final = percentChange()
        connect(final, 'input_1', prev, 'value')
        connect(final, 'input_2', open, 'value')
        break
      }
      case 'distanceFromVwap': {
        const vwap = make(freshId(`${metric.id}__vwap`, taken), 'vwap', {}, -120)
        price(vwap, 'input_1')
        owners.set(vwap.id, metric.id)
        added.push(vwap.node)
        final = percentChange()
        connect(final, 'input_1', vwap.id, 'vwap')
        price(final, 'input_2', 'close')
        break
      }
      case 'positionInDayRange': {
        const low = level('low-of-day', 'low', -120)
        const high = level('high-of-day', 'high', 120)
        final = make(metric.id, 'customPositionInRange', {}, 0)
        price(final, 'input_1', 'close')
        connect(final, 'input_2', low, 'value')
        connect(final, 'input_3', high, 'value')
        break
      }
      default:
        continue
    }
    replaced.set(metric.id, final.node)
  }

  if (replaced.size === 0) return { nodes, edges, owners }
  return {
    nodes: [...nodes.map((n) => replaced.get(n.id) ?? n), ...added],
    edges: [
      // Wires into a lowered metric are rebuilt above; wires out of it leave
      // from its last node's output.
      ...edges
        .filter((e) => !replaced.has(e.target))
        .map((e) =>
          outputOf.has(e.source) ? ({ ...e, sourceHandle: outputOf.get(e.source) } as E) : e,
        ),
      ...addedEdges,
    ],
    owners,
  }
}
